import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './ResetPassword.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(true);

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setError('Invalid or missing reset token. Please request a new password reset.');
    }
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          newPassword: formData.password
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setError(data.message || 'Failed to reset password. The link may have expired.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-container">
          <div className="reset-password-box">
            <div className="error-state">
              <span className="material-icons error-icon">error_outline</span>
              <h1>Invalid Reset Link</h1>
              <p>The password reset link is invalid or has expired.</p>
              <button 
                className="btn-primary full-width" 
                onClick={() => navigate('/forgot-password')}
              >
                Request New Reset Link
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        <div className="reset-password-box">
          <div className="reset-password-header">
            <h1 className="reset-password-title">Create New Password</h1>
            <p className="reset-password-subtitle">
              {success 
                ? "Your password has been reset successfully!"
                : "Enter your new password below"
              }
            </p>
          </div>

          {!success ? (
            <form onSubmit={handleSubmit} className="reset-password-form">
              {error && (
                <div className="error-message">
                  <span className="material-icons">error</span>
                  {error}
                </div>
              )}

              <div className="input-group">
                <span className="material-icons">lock</span>
                <input
                  type="password"
                  name="password"
                  placeholder="New password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  autoFocus
                />
              </div>

              <div className="input-group">
                <span className="material-icons">lock_outline</span>
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm new password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="password-requirements">
                <p className="requirements-title">Password must contain:</p>
                <ul>
                  <li className={formData.password.length >= 8 ? 'valid' : ''}>
                    At least 8 characters
                  </li>
                  <li className={/(?=.*[a-z])/.test(formData.password) ? 'valid' : ''}>
                    One lowercase letter
                  </li>
                  <li className={/(?=.*[A-Z])/.test(formData.password) ? 'valid' : ''}>
                    One uppercase letter
                  </li>
                  <li className={/(?=.*\d)/.test(formData.password) ? 'valid' : ''}>
                    One number
                  </li>
                </ul>
              </div>

              <button type="submit" className="btn-primary full-width" disabled={loading}>
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </button>

              <div className="reset-password-footer">
                <a href="#" onClick={(e) => { e.preventDefault(); navigate('/'); }}>
                  <span className="material-icons">arrow_back</span>
                  Back to Login
                </a>
              </div>
            </form>
          ) : (
            <div className="success-message">
              <div className="success-icon">
                <span className="material-icons">check_circle</span>
              </div>
              <h2>Password Reset Successful!</h2>
              <p>Your password has been changed. You can now log in with your new password.</p>
              <p className="redirect-message">Redirecting to login page...</p>
              <button 
                className="btn-primary full-width" 
                onClick={() => navigate('/')}
              >
                Go to Login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;


